import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormW2VsW4 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form W-2 vs W-4: Understanding the Key Differences"
          description="Learn the differences between Form W-2 and W-4, their uses, deadlines, penalties and who files each form to stay compliant. E-file W-2s quickly with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-w2-vs-w4/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form W-2</span>
          </span>{' '}
          vs{' '}
          <span className="highlight-container">
            <span className="highlight">Form W-4</span>
          </span>
          : Key Differences
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Understand the purpose, deadlines, and key differences between
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          W-2 and W-4—file with confidence!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=fw2vsw4`}
          >
            E-file Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                  minHeight: '100%',
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-w2-vs-w4/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form W-2 vs W-4 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <Link
                          href="/resources/information-returns/form-w-2-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form W-2
                        </Link>
                        {
                          ' reports an employee’s annual wages and taxes withheld, while Form W-4 tells employers how much federal income tax to withhold from each paycheck.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Form W-2 is completed by the '}
                        <strong style={{ fontWeight: 600 }}>employer</strong>
                        {', while Form W-4 is completed by the '}
                        <strong style={{ fontWeight: 600 }}>employee</strong>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>When Used:</strong>
                        {
                          ' Form W-2 is issued annually after the tax year ends, while Form W-4 is completed when starting a job or updating withholding information.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Filed With:</strong>
                        {' Form W-2 is submitted to the '}
                        <strong style={{ fontWeight: 600 }}>
                          Social Security Administration (SSA) and IRS
                        </strong>
                        , while Form W-4 is kept by the employer and is not sent
                        to the IRS or SSA.
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Form W2 summarizes wages and '}
                        withholding for
                        {' tax filing, while Form W-4 '}
                        determines
                        {' the amount of tax withheld from each paycheck.'}
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Form W-2 must '}
                        generally be furnished
                        {' to employees and filed with the SSA by '}
                        <strong style={{ fontWeight: 600 }}>
                          January 31 each year
                        </strong>
                        {
                          ', while Form W-4 changes should be applied by the employer within the required payroll '
                        }
                        timeframe .
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                {/* <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                 
                </Stack> */}

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/resources/information-returns/form-w-2-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form W-2
                  </Link>{' '}
                  and Form W-4 are both essential IRS tax forms used in the
                  employee payroll process, but they serve very different
                  purposes. Employees often confuse these two forms because they
                  both relate to income and taxes. However, knowing how each
                  form works is critical for accurate tax withholding and
                  correct tax filing.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form W-4 is used to set up tax withholding, while Form W-2 is
                  used to report wages and taxes withheld at the end of the
                  year. Understanding the difference helps both employers and
                  employees avoid tax errors and penalties.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is the purpose of these forms?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Form W-2 vs W-4: Key Difference
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#canan" sx={{ color: '#0202ff' }}>
                          Can an Employee Have Both W-2 and W-4?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#under" sx={{ color: '#0202ff' }}>
                          Understanding Form W-4 and W-2 for New Employee
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatare" sx={{ color: '#0202ff' }}>
                          What are the deadlines for submitting W-2 and W-4
                          forms?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pen" sx={{ color: '#0202ff' }}>
                          Penalties for Late or Incorrect W-2 Filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatis"
                >
                  What is the purpose of these forms?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form W-2:</span> Used by
                    employers to report an employee’s total wages and taxes
                    withheld for the year.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form W-4:</span> Used by
                    employees to tell their employer how much federal income tax
                    should be withheld from their paycheck.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="key"
                >
                  Form W2 vs W4: Key Difference
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Aspect
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form W2
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form W4
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Purpose
                          </TableCell>

                          <TableCell>
                            Reports the employee’s actual annual wages and taxes
                            withheld for the year. Used to file federal and
                            state tax returns.
                          </TableCell>

                          <TableCell>
                            Tells the employer how much federal income tax to
                            withhold from each paycheck throughout the year
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Completed by
                          </TableCell>

                          <TableCell>Employer</TableCell>

                          <TableCell>Employee</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            When it is completed
                          </TableCell>

                          <TableCell>
                            At the end of every tax year; must be issued by
                            January 31.
                          </TableCell>

                          <TableCell>
                            When starting a new job or when updating withholding
                            (after life changes such as marriage, dependents, or
                            a second job).
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who receives it
                          </TableCell>

                          <TableCell>
                            Employee, Social Security Administration (SSA) and
                            IRS.
                          </TableCell>

                          <TableCell sx={{ textAlign: 'left' }}>
                            <ul>
                              <li>W-4 is kept only by the employer </li>
                              <li>It is NOT sent to the IRS or SSA </li>
                            </ul>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            How does it Affect taxes?
                          </TableCell>

                          <TableCell>
                            Shows final income and withholding amounts used to
                            prepare the annual tax return
                          </TableCell>

                          <TableCell>
                            Determines how much federal income tax is taken out
                            of each paycheck. The withholding affects whether
                            you owe taxes or receive a refund when you file your
                            return.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Frequency
                          </TableCell>

                          <TableCell>
                            Issued once a year for every employee
                          </TableCell>

                          <TableCell>
                            Completed as needed, such as when your personal or
                            financial situation changes.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Impact on Paycheck
                          </TableCell>

                          <TableCell>
                            Does not affect current or future paychecks; it
                            summarizes the past year.
                          </TableCell>

                          <TableCell>
                            Directly impacts each paycheck by either increasing
                            or decreasing federal withholding
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Role in Payroll
                          </TableCell>

                          <TableCell>
                            It serves as an employer&apos;s annual record of
                            wages and taxes withheld.
                          </TableCell>

                          <TableCell>
                            Guides payroll throughout the year to withhold the
                            correct amount of federal income tax
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Used When Filing Taxes?
                          </TableCell>

                          <TableCell>
                            Yes, employees must use the W-2 to prepare & file
                            their tax returns
                          </TableCell>

                          <TableCell>
                            Directly impacts each paycheck by either increasing
                            or decreasing federal withholding
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Includes Employee Preferences?
                          </TableCell>

                          <TableCell>
                            No, it only reports what happened during the year.
                          </TableCell>

                          <TableCell>
                            Yes, it includes withholding selections like
                            dependents, filing status, and extra withholding.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="canan"
                >
                  Can an Employee Have Both W-2 and W-4?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Yes,</span> an employee can
                  have both Form W-2 and Form W-4 because the two forms serve
                  different purposes.
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form W-4</span> is
                    completed by the employee when they start a job or updating
                    tax withholding. It tells the employer how much federal
                    income tax to withhold from each paycheck.{' '}
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}> Form W-2</span> is
                    prepared by the employer at the end of the year to report
                    the employee’s total wages and the taxes that were withheld
                    based on the W-4 information.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="under"
                >
                  Understanding Form W-4 and W-2 for New Employees
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  When starting a new job, employees are required to{' '}
                  <span style={{ fontWeight: 600 }}> complete Form W-4,</span>{' '}
                  so their employer can correctly withhold federal income tax
                  from each paycheck. The Form W-2 does not get filled out by
                  the employee. Instead, it is provided by the employer at the
                  close of the year and represents a summary of total wages
                  earned and taxes withheld for tax filing purposes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatare"
                >
                  What are the deadlines for filing W-2 and W-4 forms?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form W-2 Deadline:</span>{' '}
                    Employers must{' '}
                    <Link
                      href="/information-returns/efile-irs-form-w2/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      file W-2 forms
                    </Link>{' '}
                    with the Social Security Administration and provide copies
                    to employees no later than January 31 every year.{' '}
                  </li>
                  <Alert
                    severity="warning"
                    icon={<EventNoteIcon />}
                    sx={{
                      mb: 2,
                      mt: 3,
                      textAlign: 'left',
                    }}
                  >
                    <b>Note:</b> For the Tax Year 2025, the deadline for W-2
                    forms is{' '}
                    <span style={{ fontWeight: 600 }}> February 2, 2026,</span>{' '}
                    since January 31 falls on weekend.
                  </Alert>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form W-4 Filing Deadline:
                    </span>{' '}
                    Employers must apply W-4 changes{' '}
                    <span style={{ fontWeight: 600 }}>
                      by the first payroll after 30 days.
                    </span>{' '}
                    The employees shall fill out or change this form when
                    starting employment and when any changes to their tax
                    situation take place.{' '}
                  </li>
                  <Alert
                    severity="warning"
                    icon={<EventNoteIcon />}
                    sx={{
                      mb: 2,
                      mt: 2,
                      textAlign: 'left',
                    }}
                  >
                    <b>Note: </b> Refer to the{' '}
                    <Link
                      href="/resources/information-returns/form-w-2-due-dates/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form W2 due date
                    </Link>{' '}
                    information to ensure timely filing and compliance with IRS
                    requirements
                  </Alert>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="pen"
                >
                  Penalties for Late or Incorrect W-2 Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Failure to file or distribute Form W2 in a timely manner may
                  result in penalties assessed by the IRS. The penalty amount
                  will vary, depending upon the lateness of the filing and
                  whether this is willful.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    $60 per form if filed within 30 days after the due date{' '}
                  </li>
                  <li>
                    $130 per form if filed after 30 days but before August
                    1{' '}
                  </li>
                  <li>
                    $340 per form if filed after August 1 or not filed at
                    all{' '}
                  </li>
                  <li>$680 or more per form for intentional disregard </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 3,
                    textAlign: 'left',
                  }}
                >
                  <b>Note:</b> You can also refer to the detailed{' '}
                  <Link
                    href="/resources/information-returns/form-w2-penalties/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    penalties for Form W-2
                  </Link>{' '}
                  in order to understand the fines imposed due to late filing,
                  corrections, and intentional non-compliance.
                </Alert>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above information given is based on the IRS
                        guidelines. For more details, please refer to the IRS
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/iw2w3.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions of Form W-2
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/fw4.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions of Form W-4.
                        </Link>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File Your W-2 with TaxZerone!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-file your W2 forms accurately through TaxZerone and simplify
              your year-end filing process.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=fw2vsw4`}
              >
                E-file Now
              </Button>
              {/* <Typography variant="body1" sx={{ mt: 1 }}>
                Takes 3 simple steps
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormW2VsW4;
